import { apiUrl } from "../api/endpoints";
import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import './details.css';
import './OrganizationDetails.css';
import Spinner from '../Components/Spinner';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate, useParams } from 'react-router-dom';
import { defaultIcon, STATIC_ICONS } from '../Constants';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { OrganizationIcon } from '../Icons';
import { useAuth } from '../hooks/AuthProvider';
import { deleteWithIcon } from '../lib/actions';

import { LanguageContext } from '../context/LanguageContext';
import orgTranslations from '../languages/organization.json';
import Alert from '../Components/Alert';
import AppDialog from '../Components/AppDialog'; 
import { ActiveUsersIcon, GroupHierarchyIcon, GroupInformationsIcon, SubOrganizationIcon, TotalDeviceIcon } from '../svg/OrganizationIcons';
import { AddNewIcon, DeleteIcon, DetailsMainIcon, VerticalDividerIcon } from '../svg/UtilsIcons';

import { EditOrganization } from './EditOrganization';
import TransferOrganizationModal from '../Components/TransferOrganizationModal';

export const OrganizationDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { lang } = useContext(LanguageContext);
    const t = orgTranslations[lang].organizationDetails;
    const isRtl = ['ar', 'ur'].includes(lang);

    const { permissions, setOrg } = useAuth();
    const canView = permissions['view_organizations'];
    const canEdit = permissions['edit_organizations'];
    const canDelete = permissions['delete_organizations'];
    const canTransfer = permissions['transfer_organizations'];

    const [openEdit, setOpenEdit] = useState(false);
    const [transfer, setTransfer] = useState({ open: false, id: 0 });

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = useCallback((type, message) => {
        setAlertState({
            open: true,
            type,
            message,
            position: isRtl ? 'left' : 'right'
        });
    }, [isRtl]);

    const resolverRef = useRef(null);
    const [dialog, setDialog] = useState({
        open: false,
        type: "alert",      // "alert" | "confirm"
        variant: "info",    // "info" | "danger" | "error"
        icon: "alert",      // "alert" | "delete"
        title: "",
        message: "",
        confirmText: "OK",
        cancelText: "Cancel",
        showCancel: false,
    });

    const closeDialog = useCallback((result = false) => {
        setDialog((d) => ({ ...d, open: false }));
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    const alert = useCallback(
        (opts) =>
            new Promise((resolve) => {
                resolverRef.current = resolve;

                setDialog({
                    open: true,
                    type: opts?.type || "alert",
                    variant: opts?.variant || "info",
                    icon: opts?.icon || "alert",
                    title: opts?.title || "",
                    message: opts?.message || "",
                    confirmText: opts?.confirmText || t.buttonOk || "OK",
                    cancelText: opts?.cancelText || t.buttonCancel || "Cancel",
                    showCancel: Boolean(opts?.showCancel),
                });
            }),
        [t.buttonCancel, t.buttonOk]
    );

    const [details, setDetails] = useState({ data: {}, loading: true });

    const refetchDetails = useCallback(() => {
        if (!canView || !id || !Number.isInteger(Number(id)) || Number(id) < 1) {
            setDetails({ data: {}, loading: false });
            return;
        }

        setDetails(prev => ({ ...prev, loading: true }));

        axios
            .get(apiUrl(`organization/get/${id}`))
            .then(res => {
                if (res.data && res.data.status === 'OK') {
                    setDetails({ data: res.data.data, loading: false });
                } else {
                    showAlert('error', t.fetchFailed || 'Failed to retrieve organization details.');
                    setDetails({ data: {}, loading: false });
                }
            })
            .catch(err => {
                console.error('API Error:', err);
                const errorResponse = err.response;
                if (errorResponse) {
                    if (errorResponse.status === 404) {
                        showAlert(
                            'error',
                            (t.notFound || 'Organization with ID {id} not found.').replace('{id}', id)
                        );
                    } else if (
                        errorResponse.status === 400 &&
                        errorResponse.data &&
                        errorResponse.data.errors
                    ) {
                        const errorMessage =
                            errorResponse.data.errors
                                .map(e => e.msg)
                                .join(' ');
                        showAlert('error', errorMessage);
                    } else {
                        showAlert('error', t.unexpectedError || 'An unexpected error occurred. Please try again later.');
                    }
                } else {
                    showAlert('error', t.networkError || 'Network error. Please check your connection.');
                }
                setDetails({ data: {}, loading: false });
            });
    }, [canView, id, showAlert, t.fetchFailed, t.networkError, t.notFound, t.unexpectedError]);

    useEffect(() => {
        refetchDetails();
    }, [refetchDetails]);

    if (!canView) {
        return (
            <div style={{ padding: '20px' }}>
                {orgTranslations[lang].noPermission}
            </div>
        );
    }

    if (details.loading) {
        return <Spinner />;
    }

    if (!details.data?.id) {
        return (
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {orgTranslations[lang].noData}
            </div>
        );
    }

    const handleEditSuccess = (updatedOrg) => {
        setOpenEdit(false);
        setDetails(prev => ({ ...prev, data: updatedOrg }));
        setOrg({ id: updatedOrg.id, name: updatedOrg.name });
        showAlert('success', t.updateSuccess || 'Organization updated successfully.');
    };

    const handleDelete = async () => {
        await deleteWithIcon(
            details.data.id,
            details.data.icon,
            'organization',
            () => navigate('/administration/organizations'),
            alert
        );
    };

    return (
        <div className="configuration org-page">
            <Alert
                open={alertState.open}
                close={() => setAlertState({ ...alertState, open: false })}
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            <AppDialog
                open={dialog.open}
                type={dialog.type}
                variant={dialog.variant}
                icon={dialog.icon}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                showCancel={dialog.showCancel}
                onConfirm={() => closeDialog(true)}
                onCancel={() => closeDialog(false)}
                onClose={() => closeDialog(false)}
            />

            {openEdit && (
                <EditOrganization
                    state={{ open: openEdit, id: details.data.id }}
                    setState={setOpenEdit}
                    onSuccess={handleEditSuccess}
                    initialData={details.data}
                />
            )}

            {transfer.open && (
                <TransferOrganizationModal
                    state={transfer}
                    setState={setTransfer}
                    refresh={refetchDetails}
                />
            )}

            <div className="asset-top-bar">
                <button
                    className="asset-back-btn"
                    onClick={() => navigate('/administration/organizations')}
                >
                    <span className="asset-back-icon">{"\u2190"}</span>
                    <span>{t.backLabel || 'Back'}</span>
                </button>

                <div className="asset-top-actions">
                    {canEdit && (
                        <button
                            className="devices-primary-btn"
                            onClick={() => setOpenEdit(true)}
                        >
                            {t.editOrganization}
                        </button>
                    )}
                    {canTransfer && Number(details.data.parent) > 0 && (
                        <button
                            className="devices-primary-btn"
                            onClick={() =>
                                setTransfer({ open: true, id: details.data.id })
                            }
                        >
                            {t.transferTitle || 'Transfer Organization'}
                        </button>
                    )}
                </div>
            </div>

            <div className="asset-hero-card org-hero-card">
                <div className="asset-hero-left">
                    <div className="asset-hero-icon org-hero-icon">
                        <DetailsMainIcon />
                    </div>

                    <div className="asset-hero-text">
                        <h1>{details.data.name}</h1>

                        <div className="asset-hero-meta">

                            {canView && (
                                <button
                                    className="org-set-active-btn"
                                    onClick={() =>
                                        setOrg({
                                            id: details.data.id,
                                            name: details.data.name
                                        })
                                    }
                                >
                                    {t.setAsActive || 'Use this Organization'}
                                </button>
                            )}

                            {canDelete && (
                                <VerticalDividerIcon />
                            )}

                            {canDelete && (
                                <button
                                    type="button"
                                    className="org-hero-delete-btn"
                                    onClick={handleDelete}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <DeleteIcon color="rgb(236, 69, 78)" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="org-main-panel">
                <div className="org-summary-row">
                    <Tooltip title={t.tooltipDevices}>
                        <div className="org-summary-card">
                            <div className="org-summary-icon org-summary-icon-total-devices">
                                <TotalDeviceIcon color={'var(--accent)'} />
                            </div>
                            <div className="org-summary-text">
                                <span className="org-summary-label">
                                    {t.totalDevices || 'Total Devices'}
                                </span>
                                <span className="org-summary-value">
                                    {details.data.device_count}
                                </span>
                            </div>
                        </div>
                    </Tooltip>

                    <Tooltip title={t.tooltipUsers}>
                        <div className="org-summary-card">
                            <div className="org-summary-icon org-summary-icon-active-users">
                                <ActiveUsersIcon color={'var(--accent)'} />
                            </div>
                            <div className="org-summary-text">
                                <span className="org-summary-label">
                                    {t.activeUsers || 'Active Users'}
                                </span>
                                <span className="org-summary-value">
                                    {details.data.user_count}
                                </span>
                            </div>
                        </div>
                    </Tooltip>

                    <Tooltip title={t.tooltipSubOrganizations}>
                        <div className="org-summary-card">
                            <div className="org-summary-icon org-summary-icon-sub-organizations">
                                <SubOrganizationIcon color={'var(--accent)'} />
                            </div>
                            <div className="org-summary-text">
                                <span className="org-summary-label">
                                    {t.subOrganizationsLabel || 'Sub Organizations'}
                                </span>
                                <span className="org-summary-value">
                                    {details.data.sub_organizations}
                                </span>
                            </div>
                        </div>
                    </Tooltip>
                </div>

                <div className="org-main-grid">
                    <section className="org-section-card">
                        <header className="org-section-header">
                            <div className="org-section-icon org-section-icon-group-informations">
                                <GroupInformationsIcon color={'var(--accent)'} />
                            </div>
                            <div className="org-section-title-block">
                                <h2>
                                    {t.groupInformationTitle || 'Group Information'}
                                </h2>
                            </div>
                        </header>

                        <div className="org-details-grid">
                            {details.data.name && (
                                <div className="org-detail-item">
                                    <div className="org-detail-label">
                                        {t.fieldName}
                                    </div>
                                    <div className="org-detail-value">
                                        {details.data.name}
                                    </div>
                                </div>
                            )}

                            {details.data.description && (
                                <div className="org-detail-item">
                                    <div className="org-detail-label">
                                        {t.fieldDescription}
                                    </div>
                                    <div className="org-detail-value">
                                        {details.data.description}
                                    </div>
                                </div>
                            )}

                            {details.data.timezone && (
                                <div className="org-detail-item">
                                    <div className="org-detail-label">
                                        {t.fieldTimezone}
                                    </div>
                                    <div className="org-detail-value">
                                        {details.data.timezone}
                                    </div>
                                </div>
                            )}

                            <div className="org-detail-item">
                                <div className="org-detail-label">
                                    {t.fieldUsers}
                                </div>
                                <div className="org-detail-value">
                                    {details.data.user_count}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="org-section-card">
                        <header className="org-section-header">
                            <div className="org-section-icon org-section-icon-group-hierarchy">
                                <GroupHierarchyIcon color={'var(--accent)'} />
                            </div>
                            <div className="org-section-title-block">
                                <h2>{t.fieldHierarchy}</h2>
                            </div>
                        </header>

                        <div className="org-details-grid org-details-grid-full">
                            <div className="org-detail-item">
                                <div className="org-detail-label">
                                    {t.fieldHierarchy}
                                </div>
                                <div className="org-detail-value">
                                    <span className="details-heirarchy-tree">
                                        {details.data?.hierarchy?.map((child, idx) => (
                                            <span
                                                className="details-hierarchy-item"
                                                key={idx}
                                                style={{ paddingInlineStart: 8 * idx }}
                                            >
                                                {idx !== 0 && (
                                                    <i
                                                        style={{
                                                            marginInlineEnd: '8px',
                                                            fontSize: 'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                                                            fontWeight: '700',
                                                            color: 'var(--accent)'
                                                        }}
                                                    >
                                                        {'\u2BB1'}
                                                    </i>
                                                )}
                                                {child.name}
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};
